/**
 * F4 (#1858 §1a, §4b; round 1 C-B3, A-B1): a turn as the client STORES it — the one persistence boundary for every
 * place the client keeps a thread beyond the screen: the snapshot it upserts, the in-memory thread list it is copied
 * into, and the localStorage buffer a failed persist leaves for the next load.
 *
 * - Nothing stored holds a label (invariant 9). `mission_planned`'s reading and progress and `part_accepted`'s reading
 *   are kept as references only, as the server's snapshot ingress (`progress.snapshot_by_reference`) keeps them; every
 *   label is rendered for the reader when the server reads the thread for them (`progress.snapshot_rendered`). A turn
 *   read back from local storage alone shows no chip and no step words (`readingPart` / `stepsOf` need the labels).
 * - No number the gatekeeper has not accepted (invariant 4). A part with no `part_accepted` yet is stored without its
 *   rows, SQL, spec or review reasons: while the turn runs, its events are cut at the first undecided one and the
 *   durable sequence rewound to before it, so a resume replays the rest from the server, which projects whatever the
 *   decision turns out to be; once the turn has ended, those events are kept in their held form. A part that was not
 *   accepted is already its public form (rule 6), and an accepted part's rows are kept.
 */
import type { LandingAgentEvent, LandingAgentTurn, WorkerResultSnapshot } from './landingAgentSlice';
import { PART, REVIEWS } from './acceptance';
import { PENDING_ACCEPTANCE } from './progress';
import { writerPersisted, writerWordsStripped } from './writerPersisted';

const isObj = (v: unknown): v is Record<string, any> => !!v && typeof v === 'object' && !Array.isArray(v);
const id = (v: unknown) => (isObj(v) ? v.id : v);
const strings = (v: unknown) => (Array.isArray(v) ? v.map(id).filter((x): x is string => typeof x === 'string') : []);
const IDS = ['sub_task_id', 'result_id', 'session_id', 'thread_id'];

function ids(event: LandingAgentEvent): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of IDS) if (typeof event[k] === 'string') out[k] = event[k];
  if (typeof event.run_seq === 'number') out.run_seq = event.run_seq;
  return out;
}

/** One reading part as references (`progress._part_refs`): ids, ISO dates and filter leaves; never a label. */
export function partRefs(part: unknown): Record<string, unknown> | null {
  if (!isObj(part) || typeof part.sub_task_id !== 'string' || (part.source !== 'plan' && part.source !== 'ran')) return null;
  const w = isObj(part.window) && typeof part.window.start === 'string' && typeof part.window.end === 'string'
    ? { start: part.window.start, end: part.window.end, ...(typeof part.window.said === 'string' ? { said: part.window.said } : {}) } : null;
  return { sub_task_id: part.sub_task_id, source: part.source,
    ...(typeof part.result_id === 'string' ? { result_id: part.result_id } : {}),
    perspective: id(part.perspective), metrics: strings(part.metrics), grouping: strings(part.grouping), window: w,
    filters: (Array.isArray(part.filters) ? part.filters : []).filter(isObj)
      .map((f) => ({ dimension: f.dimension, operator: f.operator, value: f.value })) };
}

/** An F4 frame by reference (`progress.by_reference`); any other frame as it is. */
export function byReference(event: LandingAgentEvent): LandingAgentEvent {
  if (event.type === 'mission_planned' && ('reading' in event || 'progress' in event)) {
    const progress = isObj(event.progress) ? event.progress : {};
    return { ...event,
      reading: { parts: (Array.isArray(event.reading?.parts) ? event.reading.parts : []).map(partRefs).filter(Boolean) },
      progress: { steps: (Array.isArray(progress.steps) ? progress.steps : []).filter(isObj)
        .map((s) => ({ id: s.id, kind: s.kind, sub_task_ids: strings(s.sub_task_ids) })),
      ...(typeof progress.usually_s === 'number' ? { usually_s: progress.usually_s } : {}),
      ...(typeof progress.longer_after_s === 'number' ? { longer_after_s: progress.longer_after_s } : {}) } };
  }
  if (event.type === PART) {
    const accepted = event.accepted === true;
    const reading = accepted ? partRefs(event.reading) : null;
    return { type: PART, ...ids(event), accepted,
      ...(accepted && Array.isArray(event.defaults) ? { defaults: event.defaults } : {}), ...(reading ? { reading } : {}) };
  }
  return event;
}

/** A held part's event as stored: its ids and status, never a row, the SQL or a reason. */
function held(event: LandingAgentEvent): LandingAgentEvent {
  if (event.type === 'worker_result') return { type: event.type, ...ids(event), status: PENDING_ACCEPTANCE };
  return { type: event.type, ...ids(event), ...(typeof event.verdict === 'string' ? { verdict: event.verdict } : {}) };
}

/** An F4 frame the stored form keeps by reference only. */
const referenced = (e: LandingAgentEvent) => (e?.type === 'mission_planned' && isObj(e.progress)) || e?.type === PART;

/**
 * Whether a turn holds F4 frames by reference only — a copy from the stored form (the localStorage buffer, a read of
 * the stored snapshot) that was never rendered for this reader. An ended one is a placeholder the thread's read takes
 * the place of (`threadsSlice.rebuildTurns`), so its chips and steps are rendered for the reader before they are shown
 * again; a running one resumes from before its references (`storedTurn`'s cut).
 */
export function needsRender(turn: any): boolean {
  // F1 (#1806): a Writer answer whose words were taken out at the sink is shown only once the server renders it
  if (turn && turn.status !== 'running' && writerWordsStripped(turn)) return true;
  if (!turn || turn.status === 'running' || !Array.isArray(turn.events)) return false;
  return turn.events.some((e: any) => {
    if (e?.type === 'mission_planned' && isObj(e.progress)) {
      const steps = Array.isArray(e.progress.steps) ? e.progress.steps : [];
      const parts = Array.isArray(e.reading?.parts) ? e.reading.parts : [];
      return steps.some((st: any) => !isObj(st?.words)) || parts.some((p: any) => !isObj(p?.perspective));
    }
    return e?.type === PART && e.accepted === true && isObj(e.reading) && !isObj(e.reading.perspective);
  });
}

/** F1 (#1806): what a stored request keeps of an earlier reply — the twin's own constant, re-exported. */
export { HISTORY_PLACEHOLDER } from './writerPersisted';

/** The turn as the client stores it (module docstring). */
/** #1499: what a client stores of an entity list is what the server persists — `rows_live` (the whole list, live
 *  only) never kept, and an entity part's rows at most the persisted 100, as `rows_preview` is. */
export const PERSISTED_ROWS = 100;
export function withoutLiveRows<T extends LandingAgentTurn>(turn: T): T {
  if (!turn) return turn;
  const live = Array.isArray(turn.events) && turn.events.some((e) => isObj(e) && 'rows_live' in e);
  const workers = turn.result && Array.isArray(turn.result.workerResults) ? turn.result.workerResults : null;
  const long = !!workers && workers.some((w) => w.entity && Array.isArray(w.rows) && w.rows.length > PERSISTED_ROWS);
  if (!live && !long) return turn;
  const events = live ? turn.events.map((e) => {
    if (!isObj(e) || !('rows_live' in e)) return e;
    const { rows_live: _dropped, ...rest } = e as Record<string, unknown>;
    return rest as LandingAgentEvent;
  }) : turn.events;
  const result = long ? { ...turn.result, workerResults: workers!.map((w) => (w.entity && Array.isArray(w.rows)
    ? { ...w, rows: w.rows.slice(0, PERSISTED_ROWS) } : w)) } : turn.result;
  return { ...turn, events, result };
}

/** An answer that finished on this screen is stored whole, as it was shown (owner, 2026-11-02): reopening its thread
 *  loads it, with nothing rendered again. `SAVED_ANSWERS=off` on the server stores it by the rules below instead; the tests here
 *  keep those rules (`setupTests`). */
const FINISHED = ['done', 'error', 'cancelled'];
function asShown<T extends LandingAgentTurn>(turn: T): T {
  const whole = withoutLiveRows(turn);
  return { ...whole, savedAsShown: true,
    events: Array.isArray(whole.events) ? whole.events.filter((e) => e?.type !== 'answer_progress') : whole.events };
}

/** Nothing more is coming for it: no ladder or delivery awaited, no run to resume, no cancel to confirm. */
function isFinal(turn: LandingAgentTurn): boolean {
  return FINISHED.includes(turn.status) && !turn.analysisAwaitingLadder && !turn.analysisDisconnected
    && !turn.analysisCancelPending && !turn.diagnosisDisconnected && !turn.diagnosisCancelPending
    && turn.result?.ladder?.delivery?.context_status !== 'pending'
    && (turn.status !== 'done' || !turn.result || !!turn.result.ladder);     // the ladder comes after the run's end
}

export function storedTurn<T extends LandingAgentTurn>(input: T): T {
  if (process.env.REACT_APP_SAVED_ANSWERS !== 'off'
    && (input?.savedAsShown || (input?.shownLive && isFinal(input)))) return asShown(input);
  // F1 (#1806): a Writer answer's words stored as the server stores them (`writerPersisted`), F4's rules after;
  // #1499: never an entity list's live rows
  const turn = withoutLiveRows(writerPersisted(input));
  if (!turn || !Array.isArray(turn.events)) return turn;
  let events = turn.events.filter((e) => e?.type !== 'answer_progress').map(byReference);
  const planned = events.some((e) => e?.type === 'mission_planned' && isObj(e.progress));
  if (!planned) return events.length === turn.events.length && events.every((e, i) => e === turn.events[i])
    ? turn : { ...turn, events };
  const decided = new Set(events.filter((e) => e?.type === PART && typeof e.sub_task_id === 'string').map((e) => e.sub_task_id));
  const undecided = (e: LandingAgentEvent) => typeof e?.sub_task_id === 'string' && !decided.has(e.sub_task_id)
    && (e.type === 'worker_result' || REVIEWS.includes(e.type));
  let sequence = turn.analysisSequence;
  // a running turn is stored up to its first frame a resume must hear again from the server: an undecided part's, or
  // an F4 frame kept only by reference (round 2: a frame before the cursor is never replayed, so a reference kept there
  // would never be rendered again) — the sequence rewound to before it, so the server replays it, rendered for the
  // viewer then. An ended turn keeps its references (the thread's read renders them, `needsRender`) and holds the rest.
  const cut = turn.status === 'running' ? events.findIndex((e) => undecided(e) || referenced(e)) : -1;
  if (cut >= 0 && typeof events[cut].run_seq === 'number') {
    events = events.slice(0, cut);
    sequence = events.reduce((n, e) => (typeof e.run_seq === 'number' && e.run_seq > n ? e.run_seq : n), 0);
  } else {
    events = events.map((e) => (undecided(e) ? held(e) : e));
  }
  const result = turn.result && Array.isArray(turn.result.workerResults)
    ? { ...turn.result, workerResults: turn.result.workerResults.map((w): WorkerResultSnapshot =>
      (decided.has(w.sub_task_id) ? w : { sub_task_id: w.sub_task_id, status: PENDING_ACCEPTANCE })) } : turn.result;
  return { ...turn, events, result, ...(sequence !== undefined ? { analysisSequence: sequence } : {}) };
}

export function storedTurns<T extends LandingAgentTurn>(turns: T[] | undefined | null): T[] {
  return (turns || []).map(storedTurn);
}
